import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Text, Heading, Badge, Button, SimpleGrid, HStack, VStack, Spinner, Center, IconButton,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, useDisclosure,
  Tabs, TabList, TabPanels, Tab, TabPanel, Alert, AlertIcon, Link, useToast,
  FormControl, FormLabel, Input, Divider, Select,
} from '@chakra-ui/react';
import { FiCheckCircle, FiPlus, FiSettings, FiTrash2, FiArrowLeft, FiSliders, FiLink, FiExternalLink, FiRefreshCw } from 'react-icons/fi';
import { SiTwilio, SiWhatsapp, SiFirebase, SiApple, SiAmazonaws, SiBrevo, SiHubspot, SiSalesforce, SiZoho, SiShopify, SiMixpanel, SiGoogleanalytics, SiPosthog, SiZapier } from 'react-icons/si';
import Header from '../../components/Header';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import API from '../../helpers/axiosInstance';
import { getApiHeaders } from '../../utils/DefaultFunction';
import { CHANNELS, providersByChannel, logoFor } from './providerCatalog';
import ProviderConfigForm from './ProviderConfigForm';
import ChannelConfigModal from './ChannelConfigModal';
import CRM_PROVIDERS from './crmCatalog';
import { ANALYTICS_PROVIDERS, AUTOMATION_PROVIDERS } from './analyticsCatalog';
import MixpanelModal from './MixpanelModal';
import { BRAND_NAME } from '../../theme/brand';

/** Logos in ./logos (png or svg) apply automatically on rebuild — drop a file named
 *  after the LOGO_FILE base (e.g. eocean.png / twilio.svg). PNG wins if both exist. */
const logoCtx = require.context('./logos', false, /\.(png|svg)$/);
const LOGO_ASSETS = logoCtx.keys().reduce((m, k) => {
  const name = k.replace(/^\.\//, '').replace(/\.(png|svg)$/, '');
  if (!m[name] || /\.png$/.test(k)) m[name] = logoCtx(k); // prefer png if both present
  return m;
}, {});
const LOGO_FILE = {
  eocean_sms: 'eocean',  eocean_ivr: 'eocean',
  twilio_sms: 'twilio',  twilio_voice: 'twilio',
  whatsapp_meta: 'whatsapp', sendgrid: 'sendgrid', aws_ses: 'aws',
  fcm: 'firebase', apns: 'apple', klaviyo: 'klaviyo', aws_sns: 'aws', xpush: 'xpush',
  messagebird: 'messagebird', sinch: 'sinch', infobip: 'infobip', plivo: 'plivo',
};

/** Bundled brand glyphs (react-icons/si) — interim fallback until a PNG is dropped in. */
const BRAND_ICONS = {
  twilio_sms:    { Icon: SiTwilio,    color: '#F22F46' },
  twilio_voice:  { Icon: SiTwilio,    color: '#F22F46' },
  whatsapp_meta: { Icon: SiWhatsapp,  color: '#25D366' },
  fcm:           { Icon: SiFirebase,  color: '#FFCA28' },
  apns:          { Icon: SiApple,     color: '#000000' },
  aws_ses:       { Icon: SiAmazonaws, color: '#FF9900' },
  brevo:         { Icon: SiBrevo,     color: '#0B996E' },
  // CRM / contact-source brands (CRM tab)
  hubspot:       { Icon: SiHubspot,    color: '#FF7A59' },
  salesforce:    { Icon: SiSalesforce, color: '#00A1E0' },
  zoho:          { Icon: SiZoho,       color: '#E42527' },
  shopify:       { Icon: SiShopify,    color: '#95BF47' },
  // Analytics + Automation brands
  mixpanel:         { Icon: SiMixpanel,        color: '#7856FF' },
  google_analytics: { Icon: SiGoogleanalytics, color: '#E37400' },
  firebase_events:  { Icon: SiFirebase,        color: '#FFCA28' },
  posthog:          { Icon: SiPosthog,         color: '#1D4AFF' },
  zapier:           { Icon: SiZapier,          color: '#FF4A00' },
};

/** Provider logo (large): bundled file (svg/png) · brand glyph · coloured-initials fallback. */
function LogoBadge({ provider }) {
  const imgSrc = LOGO_ASSETS[LOGO_FILE[provider.id]] || logoFor(provider.id);
  const brand = BRAND_ICONS[provider.id];
  if (imgSrc) {
    return (
      <Box w={16} h={16} borderRadius="xl" bg="white" border="1px solid" borderColor="gray.100"
        display="flex" alignItems="center" justifyContent="center" p="10px">
        <img src={imgSrc} alt={provider.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
      </Box>
    );
  }
  if (brand) {
    const { Icon, color } = brand;
    return (
      <Flex align="center" justify="center" w={16} h={16} borderRadius="xl" bg="white"
        border="1px solid" borderColor="gray.100">
        <Icon size={32} color={color} />
      </Flex>
    );
  }
  return (
    <Flex align="center" justify="center" w={16} h={16} borderRadius="xl" color="white"
      fontWeight="700" fontSize="lg" bg={provider.logoBg || '#64748b'}>
      {provider.logoText || provider.name.slice(0, 2)}
    </Flex>
  );
}

/** Multi-account manager — list every account configured for a provider + Add Account. */
function AccountManager({ provider, accounts, onAdd, onEdit, onRemove }) {
  const noun = provider.channel === 'sms' ? 'SMS' : provider.channel === 'email' ? 'Email'
    : provider.channel === 'voice' ? 'Voice' : provider.name;
  return (
    <VStack align="stretch" spacing={3}>
      <Text fontSize="sm" color="gray.500">Manage multiple {noun} accounts — connect as many as you need.</Text>
      <Flex justify="space-between" align="center">
        <Text fontSize="sm" fontWeight="700" color="gray.700">{noun} Accounts</Text>
        <Button size="sm" leftIcon={<FiPlus />} bg="var(--brand-primary)" color="white" _hover={{ bg: 'var(--brand-primary-hover)' }} onClick={onAdd}>
          Add Account
        </Button>
      </Flex>
      {accounts.length === 0 ? (
        <Box border="1px dashed" borderColor="gray.300" borderRadius="lg" py={8} textAlign="center">
          <Text fontSize="sm" color="gray.500" fontWeight="500">No accounts configured</Text>
          <Text fontSize="xs" color="gray.400" mt={1}>Click "Add Account" to configure your first {noun} account.</Text>
        </Box>
      ) : (
        accounts.map((a) => (
          <Flex key={a.id} p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" justify="space-between" align="center"
            _hover={{ borderColor: 'blue.200' }}>
            <Box minW={0}>
              <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>{a.account_name || 'Account'}</Text>
              <Text fontSize="xs" color="gray.400">Connected{a.created_at ? ` · ${new Date(a.created_at).toLocaleDateString()}` : ''}</Text>
            </Box>
            <HStack spacing={1} flexShrink={0}>
              <IconButton size="xs" variant="ghost" icon={<FiSettings />} aria-label="Edit account" onClick={() => onEdit(a)} />
              <IconButton size="xs" variant="ghost" color="red.500" _hover={{ bg: 'red.50' }} icon={<FiTrash2 />} aria-label="Remove account" onClick={() => onRemove(a)} />
            </HStack>
          </Flex>
        ))
      )}
    </VStack>
  );
}

/** Catalog card for Analytics / Automation providers — logo, name, group, and a
 *  Connect that opens an info modal (the per-provider ingestion backend lands later). */
function InfoProviderCard({ provider, onConnect, connected }) {
  return (
    <Box p={4} bg="white" borderRadius="xl" border="1px solid" borderColor={connected ? 'green.200' : 'gray.200'}
      boxShadow="0 1px 4px rgba(0,0,0,0.04)" transition="all .15s" _hover={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
      <Flex justify="space-between" align="start" mb={3}>
        <LogoBadge provider={provider} />
        {connected
          ? <Badge bg="#e7f6ec" color="#1f9d57" borderRadius="full" px="9px" py="2px" fontSize="11px" fontWeight="600" textTransform="none" display="inline-flex" alignItems="center" gap="4px"><FiCheckCircle size={12} /> Connected</Badge>
          : <Badge bg="gray.100" color="gray.500" borderRadius="full" px="9px" py="2px" fontSize="11px" fontWeight="600" textTransform="none">Not Connected</Badge>}
      </Flex>
      <HStack mb={1} spacing={2}>
        <Text fontWeight="600" fontSize="sm" color="gray.800">{provider.name}</Text>
        {provider.recommended && (
          <Badge bg="var(--brand-tint)" color="var(--brand-primary)" borderRadius="full" px="8px" py="2px" fontSize="10px" fontWeight="600" textTransform="none" letterSpacing="0.2px">Recommended</Badge>
        )}
        <Badge bg="gray.50" color="gray.500" borderRadius="full" px="8px" py="2px" fontSize="10px" fontWeight="500" textTransform="none">{provider.group}</Badge>
      </HStack>
      <Text fontSize="xs" color="gray.500" minH="32px">{provider.tagline}</Text>
      <Button mt={3} w="full" size="sm" colorScheme="blue" variant={connected ? 'outline' : 'solid'}
        bg={connected ? 'white' : 'var(--brand-primary)'} color={connected ? 'var(--brand-primary)' : 'white'}
        leftIcon={connected ? <FiSettings /> : <FiPlus />} onClick={() => onConnect(provider)}>
        {connected ? 'Manage' : 'Connect'}
      </Button>
    </Box>
  );
}

export default function Integrations() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);            // { provider }
  const [editAccount, setEditAccount] = useState(null);  // null=manager · 'new'=add · {row}=edit
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cfgModal = useDisclosure();                       // per-channel routing configuration popup
  const [cfgChannel, setCfgChannel] = useState(null);     // which channel the popup configures
  const crmModal = useDisclosure();                       // CRM connect popup
  const [crmActive, setCrmActive] = useState(null);       // which CRM provider is being connected
  const [crmConnections, setCrmConnections] = useState([]); // this tenant's CRM connections
  const [crmConfigured, setCrmConfigured] = useState({});   // which providers have server app creds
  const [shopInput, setShopInput] = useState('');           // Shopify store domain
  const [tokenInput, setTokenInput] = useState('');         // static access token (Private App etc.)
  const [instanceInput, setInstanceInput] = useState('');   // Salesforce instance URL
  const [apiDomainInput, setApiDomainInput] = useState(''); // Zoho API domain
  const [zohoRegion, setZohoRegion] = useState('us');       // Zoho data center (per connection)
  const infoModal = useDisclosure();                        // Analytics/Automation info popup
  const [infoActive, setInfoActive] = useState(null);       // which Analytics/Automation provider
  const mixpanelModal = useDisclosure();                    // Mixpanel connect/import popup
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get(`${process.env.REACT_APP_API_URL}/channel_api_config/my-list`, { headers: getApiHeaders() });
      const rows = (r.data?.data || []).map((row) => {
        let cfg = {};
        try { cfg = typeof row.api_config === 'string' ? JSON.parse(row.api_config) : (row.api_config || {}); } catch (_e) { /* invalid JSON — keep empty config */ }
        return { ...row, api_config: cfg };
      });
      setConfigs(rows);
    } catch (e) { setConfigs([]); } finally { setLoading(false); }
  }, []);

  const loadCrm = useCallback(async () => {
    try {
      const r = await API.get(`${process.env.REACT_APP_API_URL}/integration/my-list`, { headers: getApiHeaders() });
      setCrmConnections(r.data?.data?.connections || []);
      setCrmConfigured(r.data?.data?.configured || {});
    } catch (e) { setCrmConnections([]); }
  }, []);

  useEffect(() => { load(); loadCrm(); }, [load, loadCrm]);

  // Surface the OAuth round-trip result (?connected= / ?error=) then clean the URL.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('connected')) {
      toast({ title: `${q.get('connected')} connected`, status: 'success', duration: 4000, isClosable: true });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (q.get('error')) {
      toast({ title: `Connection failed: ${q.get('error')}`, status: 'error', duration: 5000, isClosable: true });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const accountsFor = (providerId) => configs.filter((c) => c.api_config?._provider === providerId);

  // Connected accounts for a whole channel (across its providers) — options for routing config.
  const accountsForChannel = (slug) => {
    const ids = new Set(providersByChannel(slug).map((p) => p.id));
    return configs
      .filter((c) => ids.has(c.api_config?._provider))
      .map((c) => ({ id: c.id, name: c.account_name || c.name || 'Account' }));
  };

  const openProvider = (provider) => {
    setActive({ provider });
    setEditAccount(provider.type === 'oauth' ? 'new' : null); // oauth → straight to Embedded Signup
    onOpen();
  };
  const removeAccount = async (a) => {
    try { await API.post(`${process.env.REACT_APP_API_URL}/channel_api_config/my-remove`, { id: a.id }, { headers: getApiHeaders() }); load(); }
    catch (_e) { /* removal failed — list stays unchanged */ }
  };
  const afterFormSaved = () => {
    load();
    if (active?.provider?.type === 'oauth') onClose(); else setEditAccount(null);
  };

  const crmConnFor = (id) => crmConnections.find((c) => c.provider === id);
  const openCrm = (provider) => {
    setShopInput(''); setTokenInput(''); setInstanceInput(''); setApiDomainInput('');
    setCrmActive(provider); crmModal.onOpen();
  };

  const openInfo = (provider) => { setInfoActive(provider); infoModal.onOpen(); };
  const openMixpanel = () => mixpanelModal.onOpen();

  const connectWithToken = async (provider) => {
    if (!tokenInput.trim()) { toast({ title: 'Paste an access token first', status: 'warning', duration: 3000 }); return; }
    const body = { token: tokenInput.trim() };
    if (provider.id === 'shopify') body.shop = shopInput.trim();
    if (provider.id === 'salesforce') body.instance_url = instanceInput.trim();
    if (provider.id === 'zoho') body.api_domain = apiDomainInput.trim();
    try {
      await API.post(`${process.env.REACT_APP_API_URL}/integration/${provider.id}/connect-token`, body, { headers: getApiHeaders() });
      toast({ title: `${provider.name} connected`, status: 'success', duration: 3500, isClosable: true });
      loadCrm(); crmModal.onClose();
    } catch (e) {
      toast({ title: e?.response?.data?.message || 'Could not connect with that token', status: 'error', duration: 4000, isClosable: true });
    }
  };

  const startCrmConnect = async (provider, { shop, region } = {}) => {
    try {
      const qs = new URLSearchParams();
      if (shop) qs.set('shop', shop);
      if (region) qs.set('region', region);
      const q = qs.toString() ? `?${qs.toString()}` : '';
      const r = await API.get(`${process.env.REACT_APP_API_URL}/integration/${provider.id}/oauth/start${q}`, { headers: getApiHeaders() });
      if (r.data?.url) { window.location.href = r.data.url; } // hand off to the provider
    } catch (e) {
      const code = e?.response?.data?.code;
      const msg = code === 'not_configured'
        ? `${provider.name} isn't configured on the server yet — app credentials are needed.`
        : code === 'shop_required' ? 'Enter your Shopify store domain first.'
        : e?.response?.data?.message || 'Could not start the connection.';
      toast({ title: msg, status: code === 'not_configured' ? 'warning' : 'error', duration: 4500, isClosable: true });
    }
  };

  const syncCrm = async (provider) => {
    toast({ title: `Syncing ${provider.name}…`, status: 'info', duration: 1500 });
    try {
      const r = await API.post(`${process.env.REACT_APP_API_URL}/integration/${provider.id}/sync`, {}, { headers: getApiHeaders() });
      const s = (r.data?.data?.stats) || {};
      toast({ title: `${provider.name} synced`, description: `${s.inserted || 0} added · ${s.updated || 0} updated · ${s.skipped || 0} skipped (no phone)`, status: 'success', duration: 6000, isClosable: true });
      loadCrm();
    } catch (e) {
      toast({ title: e?.response?.data?.message || 'Sync failed', status: 'error', duration: 4000, isClosable: true });
    }
  };

  const disconnectCrm = async (provider) => {
    try { await API.post(`${process.env.REACT_APP_API_URL}/integration/${provider.id}/disconnect`, {}, { headers: getApiHeaders() }); loadCrm(); crmModal.onClose(); }
    catch (_e) { /* disconnect failed — modal stays open */ }
  };

  return (
    <>
      <Header />
      <BoxWrapper>
        <Tabs colorScheme="blue" variant="line" isLazy>
          <TabList mb={2}>
            <Tab fontSize="sm" fontWeight="600">Communication</Tab>
            <Tab fontSize="sm" fontWeight="600">CRM</Tab>
            <Tab fontSize="sm" fontWeight="600">Analytics</Tab>
            <Tab fontSize="sm" fontWeight="600">Automation</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0} pt={5}>
        <Box mb={6}>
          <Heading size="md" color="gray.800">Channel Integrations</Heading>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Connect your preferred providers — just enter the credentials and you're ready to send.
          </Text>
        </Box>

        {loading ? (
          <Center py={20}><Spinner size="xl" color="blue.500" thickness="4px" /></Center>
        ) : (
          CHANNELS.map((ch) => {
            const provs = providersByChannel(ch.slug);
            if (!provs.length) return null;
            const chanAccounts = accountsForChannel(ch.slug);
            // SMS always offers config (rich routing); other channels only when there's >1 to switch.
            const showConfig = ch.slug === 'sms' ? chanAccounts.length >= 1 : chanAccounts.length > 1;
            return (
              <Box key={ch.slug} mb={8}>
                <Flex justify="space-between" align="center" mb={3}>
                  <Heading size="sm" color="gray.700">
                    {ch.name} <Text as="span" fontSize="xs" color="gray.400" fontWeight="400">— {ch.tagline}</Text>
                  </Heading>
                  {showConfig && (
                    <Button size="sm" variant="outline" colorScheme="blue" leftIcon={<FiSliders />}
                      onClick={() => { setCfgChannel(ch); cfgModal.onOpen(); }}>
                      Configuration
                    </Button>
                  )}
                </Flex>
                <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4}>
                  {provs.map((p) => {
                    const accts = accountsFor(p.id);
                    const connected = accts.length > 0;
                    return (
                      <Box key={p.id} p={4} bg="white" borderRadius="xl" border="1px solid"
                        borderColor={connected ? 'green.200' : 'gray.200'} boxShadow="0 1px 4px rgba(0,0,0,0.04)"
                        transition="all .15s" _hover={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                        <Flex justify="space-between" align="start" mb={3}>
                          <LogoBadge provider={p} />
                          {connected
                            ? <Badge bg="#e7f6ec" color="#1f9d57" borderRadius="full" px="9px" py="2px" fontSize="11px" fontWeight="600" textTransform="none" display="inline-flex" alignItems="center" gap="4px"><FiCheckCircle size={12} /> Connected</Badge>
                            : <Badge bg="gray.100" color="gray.500" borderRadius="full" px="9px" py="2px" fontSize="11px" fontWeight="600" textTransform="none">Not Connected</Badge>}
                        </Flex>
                        <HStack mb={1} spacing={2}>
                          <Text fontWeight="600" fontSize="sm" color="gray.800">{p.name}</Text>
                          {p.recommended && (
                            <Badge bg="var(--brand-tint)" color="var(--brand-primary)" borderRadius="full" px="8px" py="2px"
                              fontSize="10px" fontWeight="600" textTransform="none" letterSpacing="0.2px">
                              Recommended
                            </Badge>
                          )}
                        </HStack>
                        <Text fontSize="xs" color="gray.500" minH="32px">{p.tagline}</Text>
                        {connected && (
                          <Text fontSize="xs" color="#1f9d57" fontWeight="500" mt={1}>
                            {accts.length} account{accts.length > 1 ? 's' : ''} connected
                          </Text>
                        )}
                        <Button mt={3} w="full" size="sm" colorScheme="blue"
                          variant={connected ? 'outline' : 'solid'}
                          bg={connected ? 'white' : 'var(--brand-primary)'} color={connected ? 'var(--brand-primary)' : 'white'}
                          leftIcon={connected ? <FiSettings /> : <FiPlus />}
                          onClick={() => openProvider(p)}>
                          {connected ? 'Manage' : 'Connect'}
                        </Button>
                      </Box>
                    );
                  })}
                </SimpleGrid>
              </Box>
            );
          })
        )}
            </TabPanel>

            <TabPanel px={0} pt={5}>
              <Box mb={6}>
                <Heading size="md" color="gray.800">CRM &amp; Contact Sources</Heading>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Connect a CRM to sync its contacts into {BRAND_NAME} — one-way for now (CRM → Contacts), matched by phone.
                </Text>
              </Box>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4}>
                {CRM_PROVIDERS.map((p) => {
                  const conn = crmConnFor(p.id);
                  const connected = !!conn;
                  return (
                    <Box key={p.id} p={4} bg="white" borderRadius="xl" border="1px solid" borderColor={connected ? 'green.200' : 'gray.200'}
                      boxShadow="0 1px 4px rgba(0,0,0,0.04)" transition="all .15s" _hover={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                      <Flex justify="space-between" align="start" mb={3}>
                        <LogoBadge provider={p} />
                        {connected
                          ? <Badge bg="#e7f6ec" color="#1f9d57" borderRadius="full" px="9px" py="2px" fontSize="11px" fontWeight="600" textTransform="none" display="inline-flex" alignItems="center" gap="4px"><FiCheckCircle size={12} /> Connected</Badge>
                          : <Badge bg="gray.100" color="gray.500" borderRadius="full" px="9px" py="2px" fontSize="11px" fontWeight="600" textTransform="none">Not Connected</Badge>}
                      </Flex>
                      <HStack mb={1} spacing={2}>
                        <Text fontWeight="600" fontSize="sm" color="gray.800">{p.name}</Text>
                        {p.recommended && (
                          <Badge bg="var(--brand-tint)" color="var(--brand-primary)" borderRadius="full" px="8px" py="2px" fontSize="10px" fontWeight="600" textTransform="none" letterSpacing="0.2px">Recommended</Badge>
                        )}
                        <Badge bg="gray.50" color="gray.500" borderRadius="full" px="8px" py="2px" fontSize="10px" fontWeight="500" textTransform="none">{p.group}</Badge>
                      </HStack>
                      <Text fontSize="xs" color="gray.500" minH="32px">{p.tagline}</Text>
                      {connected && conn.last_sync_at && (
                        <Text fontSize="xs" color="#1f9d57" fontWeight="500" mt={1}>
                          Last sync {new Date(conn.last_sync_at).toLocaleString()}
                          {conn.last_stats ? ` · ${(conn.last_stats.inserted || 0) + (conn.last_stats.updated || 0)} contacts` : ''}
                        </Text>
                      )}
                      {connected ? (
                        <HStack mt={3} spacing={2}>
                          <Button flex={1} size="sm" colorScheme="blue" bg="var(--brand-primary)" color="white" leftIcon={<FiRefreshCw />} onClick={() => syncCrm(p)}>Sync now</Button>
                          <Button size="sm" variant="outline" colorScheme="red" onClick={() => disconnectCrm(p)}>Disconnect</Button>
                        </HStack>
                      ) : (
                        <Button mt={3} w="full" size="sm" colorScheme="blue" bg="var(--brand-primary)" color="white" leftIcon={<FiPlus />} onClick={() => openCrm(p)}>Connect</Button>
                      )}
                    </Box>
                  );
                })}
              </SimpleGrid>
            </TabPanel>

            <TabPanel px={0} pt={5}>
              <Box mb={6}>
                <Heading size="md" color="gray.800">Analytics &amp; Events</Heading>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Bring your product analytics &amp; event data into {BRAND_NAME} for segmentation and journeys.
                </Text>
              </Box>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4}>
                {ANALYTICS_PROVIDERS.map((p) => (
                  <InfoProviderCard key={p.id} provider={p}
                    connected={p.id === 'mixpanel' && !!crmConnFor('mixpanel')}
                    onConnect={(prov) => (prov.id === 'mixpanel' ? openMixpanel() : openInfo(prov))} />
                ))}
              </SimpleGrid>
            </TabPanel>

            <TabPanel px={0} pt={5}>
              <Box mb={6}>
                <Heading size="md" color="gray.800">Automation</Heading>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Connect {BRAND_NAME} to thousands of apps through automation platforms.
                </Text>
              </Box>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4}>
                {AUTOMATION_PROVIDERS.map((p) => (
                  <InfoProviderCard key={p.id} provider={p} onConnect={openInfo} />
                ))}
              </SimpleGrid>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </BoxWrapper>

      <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md" display="flex" alignItems="center" gap={2}>
            {active && active.provider.type !== 'oauth' && editAccount !== null && (
              <IconButton size="xs" variant="ghost" icon={<FiArrowLeft />} aria-label="Back" onClick={() => setEditAccount(null)} />
            )}
            {editAccount && editAccount !== 'new'
              ? `Edit ${active?.provider?.name} account`
              : editAccount === 'new'
                ? `Add ${active?.provider?.name} account`
                : `Configure ${active?.provider?.name}`}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {active && (
              active.provider.type === 'oauth' || editAccount !== null ? (
                <ProviderConfigForm
                  provider={active.provider}
                  existing={editAccount && editAccount !== 'new' ? editAccount : null}
                  onSaved={afterFormSaved}
                  onCancel={active.provider.type === 'oauth' ? onClose : () => setEditAccount(null)}
                />
              ) : (
                <AccountManager
                  provider={active.provider}
                  accounts={accountsFor(active.provider.id)}
                  onAdd={() => setEditAccount('new')}
                  onEdit={(a) => setEditAccount(a)}
                  onRemove={removeAccount}
                />
              )
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <ChannelConfigModal
        isOpen={cfgModal.isOpen}
        onClose={cfgModal.onClose}
        channel={cfgChannel}
        accounts={cfgChannel ? accountsForChannel(cfgChannel.slug) : []}
        onSaved={load}
      />

      <Modal isOpen={crmModal.isOpen} onClose={crmModal.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Connect {crmActive?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Alert status="info" borderRadius="md" fontSize="sm" mb={3}>
              <AlertIcon />
              Syncs your {crmActive?.name} contacts into {BRAND_NAME} Contacts (one-way). Matched by phone; email-only records are skipped.
            </Alert>
            {crmActive?.id === 'shopify' && (
              <FormControl mb={3} isRequired>
                <FormLabel fontSize="sm" mb={1}>Store domain</FormLabel>
                <Input size="sm" placeholder="my-store.myshopify.com" value={shopInput} onChange={(e) => setShopInput(e.target.value)} />
              </FormControl>
            )}
            {crmActive?.id === 'zoho' && (
              <FormControl mb={3}>
                <FormLabel fontSize="sm" mb={1}>Data center</FormLabel>
                <Select size="sm" value={zohoRegion} onChange={(e) => setZohoRegion(e.target.value)}>
                  <option value="us">United States (.com)</option>
                  <option value="eu">Europe (.eu)</option>
                  <option value="in">India (.in)</option>
                  <option value="au">Australia (.com.au)</option>
                  <option value="jp">Japan (.jp)</option>
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>Pick the region your Zoho account is hosted in (check your Zoho URL).</Text>
              </FormControl>
            )}
            <Button w="full" bg="var(--brand-primary)" color="white" _hover={{ bg: 'var(--brand-primary-hover)' }} leftIcon={<FiLink />}
              onClick={() => startCrmConnect(crmActive, { shop: shopInput, region: crmActive?.id === 'zoho' ? zohoRegion : undefined })}>
              Connect with {crmActive?.name}
            </Button>
            {crmConfigured[crmActive?.id] === false && (
              <Text fontSize="xs" color="orange.500" mt={2}>
                Server app credentials for {crmActive?.name} aren’t configured yet — connecting will tell you exactly what’s needed.
              </Text>
            )}

            <Divider my={3} />
            <Text fontSize="xs" fontWeight="600" color="gray.600" mb={2}>
              Or paste an access token{crmActive?.id === 'hubspot' ? ' (HubSpot Private App)' : ''} — no OAuth app needed
            </Text>
            {crmActive?.id === 'salesforce' && (
              <Input size="sm" mb={2} placeholder="Instance URL — https://yourorg.my.salesforce.com" value={instanceInput} onChange={(e) => setInstanceInput(e.target.value)} />
            )}
            {crmActive?.id === 'zoho' && (
              <Input size="sm" mb={2} placeholder="API domain — https://www.zohoapis.com" value={apiDomainInput} onChange={(e) => setApiDomainInput(e.target.value)} />
            )}
            <Input size="sm" mb={2} placeholder="Access token" value={tokenInput} onChange={(e) => setTokenInput(e.target.value)} />
            <Button w="full" size="sm" variant="outline" colorScheme="blue" onClick={() => connectWithToken(crmActive)}>
              Connect with token
            </Button>

            {crmActive?.docs && (
              <Link href={crmActive.docs} isExternal fontSize="xs" color="var(--brand-primary)" mt={2} display="inline-block">
                {crmActive?.name} developer docs <FiExternalLink style={{ display: 'inline', verticalAlign: '-1px' }} />
              </Link>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={infoModal.isOpen} onClose={infoModal.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">{infoActive?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Alert status="info" borderRadius="md" fontSize="sm" mb={3}>
              <AlertIcon />{infoActive?.tagline}.
            </Alert>
            <Text fontSize="sm" color="gray.600">
              This brings your {infoActive?.name} {infoActive?.group === 'iPaaS' ? 'workflows' : 'events'} into {BRAND_NAME}.
              The connect flow for this provider is being wired up — it'll be one-click here soon.
            </Text>
            {infoActive?.docs && (
              <Link href={infoActive.docs} isExternal fontSize="xs" color="var(--brand-primary)" mt={3} display="inline-block">
                {infoActive?.name} docs <FiExternalLink style={{ display: 'inline', verticalAlign: '-1px' }} />
              </Link>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <MixpanelModal
        isOpen={mixpanelModal.isOpen}
        onClose={mixpanelModal.onClose}
        connected={!!crmConnFor('mixpanel')}
        onChanged={loadCrm}
      />
    </>
  );
}
